import React, { FC, Dispatch, SetStateAction, useEffect } from "react";
import { Button, Nav, Tab, TabContainer } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SolutionSetup from "./SolutionSetup";
import { SolutionType } from "../store/slices/settingsSlice";
import SolutionIcon from "../../assets/icons/Icon_Library/solutions icons/app_colouring/sol_resource_managment_fulfilment-01.svg2";
import MonitoringIcon from "../../assets/icons/Icon_Library/solutions icons/app_colouring/sol_resource_managment_fulfilment-01.svg2";
import TestingIcon from "../../assets/icons/Icon_Library/solutions icons/app_colouring/sol_resource_managment_fulfilment-01.svg2";
import CreateSolutionForm from "./CreateSolutionForm";
import SelectSolutionProject from "../Library/SelectSolutionProject";

type ErrorMap = Record<string, string[] | null>;

type SolutionTabConfig = {
  id: string;
  name: string;
  Icon: React.ComponentType<any>;
  Component: React.ComponentType<any>;
};

const solutionTabs: SolutionTabConfig[] = [
  {
    id: "solution-setup",
    name: "SOLUTION SETUP",
    Icon: SolutionIcon,
    Component: CreateSolutionForm,
  },
  {
    id: "solution-components",
    name: "SOLUTION COMPONENTS",
    Icon: MonitoringIcon,
    Component: SelectSolutionProject,
  },
  {
    id: "user-journey",
    name: "USER JOURNEY",
    Icon: TestingIcon,
    Component: (p) => (
      <div className="bg-secondary-subtle p-3">
        {!!p.solution.Components?.length ? (
          <SolutionSetup {...p} />
        ) : (
          <div className="text-center text-muted">
            <p className="mb-0">No components added yet.</p>
            <p className="mb-0">
              Please add components in the "SOLUTION COMPONENTS" tab.
            </p>
          </div>
        )}
      </div>
    ),
  },
];

const defaultSolutionTab = "solution-setup";

interface SolutionTabsProps {
  solution: SolutionType;
  setSolution: (solution: SolutionType) => void;
  saveSolution: () => Promise<boolean>;
  errors?: ErrorMap;
  setErrors?: Dispatch<SetStateAction<ErrorMap>>;
}

const SolutionTabs: FC<SolutionTabsProps> = ({
  solution,
  setSolution,
  saveSolution,
  errors,
  setErrors,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { solutionUuid, solutionTab } = useParams<{
    solutionUuid: string;
    solutionTab?: string;
  }>();
  const requestedTab = solutionTab || defaultSolutionTab;
  const canShowRequestedTab =
    !!solution.Uuid || !!(solutionUuid && solutionUuid !== "new");
  const currentTab =
    solutionTabs.some((tab) => tab.id === requestedTab) &&
    (canShowRequestedTab || requestedTab === defaultSolutionTab)
      ? requestedTab
      : defaultSolutionTab;
  const currentTabIndex = solutionTabs.findIndex(
    (tab) => tab.id === currentTab
  );

  const setCurrentTab = (tabId: string) => {
    const nextPath = `/library/solutions/${solutionUuid}/edit${
      tabId === defaultSolutionTab ? "" : `/${tabId}`
    }`;
    navigate({ pathname: nextPath, search: location.search });
  };

  useEffect(() => {
    if (currentTab === requestedTab) return;
    const nextPath = `/library/solutions/${solutionUuid}/edit${
      currentTab === defaultSolutionTab ? "" : `/${currentTab}`
    }`;
    navigate({ pathname: nextPath, search: location.search }, { replace: true });
  }, [currentTab, location.search, navigate, requestedTab, solutionUuid]);

  return (
    <div>
      <TabContainer id="solution-tabs" activeKey={currentTab} mountOnEnter unmountOnExit>
        <Nav
          className="bg-colored-reverse align-items-center  gap-3 justify-content-center"
          activeKey={currentTab}
          onSelect={(selectedKey) => {
            if (selectedKey) {
              setCurrentTab(selectedKey);
            }
          }}
        >
          {solutionTabs.map((tab) => (
            <Nav.Item key={tab.id}>
              <Nav.Link
                className={` position-relative p-3 ${
                  currentTab === tab.id ? "bg-white" : ""
                }`}
                eventKey={tab.id}
                disabled={!solution.Uuid}
              >
                <div
                  className={`position-absolute top-0 start-50 translate-middle rounded-pill py-1 px-2 text-spaced fw-bold w-auto text-xs  ${
                    currentTab === tab.id
                      ? "bg-colored icon-white"
                      : "bg-white icon-purple"
                  }`}
                >
                  <tab.Icon
                    width="1rem"
                    className={`${currentTab === tab.id ? "white" : "purple"}`}
                  />
                </div>
                <div
                  className={`  ${
                    currentTab === tab.id ? "text-purple" : "text-white"
                  }`}
                >
                  {tab.name}
                </div>
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <Tab.Content className="">
          {solution &&
            solutionTabs.map((tab) => (
              <Tab.Pane key={tab.id} eventKey={tab.id}>
                <tab.Component
                  solution={solution}
                  setSolution={setSolution}
                  errors={errors}
                  setErrors={setErrors}
                />
              </Tab.Pane>
            ))}
        </Tab.Content>
      </TabContainer>
      <div className="d-flex justify-content-end">
        <div>
          {currentTabIndex > 0 && (
            <Button
              type="submit"
              className="btn btn-colored-reverse rounded-pill fw-bold px-4 my-2"
              onClick={() =>
                setCurrentTab(solutionTabs[currentTabIndex - 1].id)
              }
            >
              PREVIOUS
            </Button>
          )}
        </div>
        <div className="ms-2">
          {currentTabIndex > -1 && currentTabIndex + 1 < solutionTabs.length && (
            <Button
              type="submit"
              className="btn btn-colored-reverse rounded-pill fw-bold px-4 my-2"
              onClick={async () => {
                if (!solution.Uuid) {
                  const res = await saveSolution();
                  if (!res) return;
                }
                setCurrentTab(solutionTabs[currentTabIndex + 1].id);
              }}
              disabled={
                !solution?.Label ||
                !solution?.Version ||
                !solution?.TagsMap?.category?.length
              }
            >
              NEXT
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SolutionTabs;
